/**
 * Binary min-heap specialised for pathfinding open-sets. Orders by primary key
 * `f`, breaking ties by secondary key `tie` (then insertion is irrelevant), which
 * makes A* output a single, deterministic optimal path.
 */
export interface HeapNode {
  /** Grid tile index. */
  readonly node: number;
  /** Primary priority (f = g + h). */
  f: number;
  /** Deterministic tie-breaker (e.g. h, or node index). */
  tie: number;
}

export class BinaryHeap {
  private readonly items: HeapNode[] = [];

  get size(): number {
    return this.items.length;
  }

  private less(a: HeapNode, b: HeapNode): boolean {
    return a.f < b.f || (a.f === b.f && a.tie < b.tie);
  }

  push(item: HeapNode): void {
    const items = this.items;
    items.push(item);
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.less(items[i] as HeapNode, items[parent] as HeapNode)) {
        [items[i], items[parent]] = [items[parent] as HeapNode, items[i] as HeapNode];
        i = parent;
      } else break;
    }
  }

  pop(): HeapNode | undefined {
    const items = this.items;
    const top = items[0];
    if (top === undefined) return undefined;
    const last = items.pop() as HeapNode;
    if (items.length > 0) {
      items[0] = last;
      this.siftDown(0);
    }
    return top;
  }

  private siftDown(start: number): void {
    const items = this.items;
    const n = items.length;
    let i = start;
    for (;;) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.less(items[l] as HeapNode, items[smallest] as HeapNode)) smallest = l;
      if (r < n && this.less(items[r] as HeapNode, items[smallest] as HeapNode)) smallest = r;
      if (smallest === i) break;
      [items[i], items[smallest]] = [items[smallest] as HeapNode, items[i] as HeapNode];
      i = smallest;
    }
  }

  clear(): void {
    this.items.length = 0;
  }
}
